import { useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { BottomSheet } from '../Common/BottomSheet'
import { TextInput } from '../Common'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { ISO_4217_CURRENCIES } from '../../utils/iso4217'
import { useNls } from '../../nls/nlsProvider'

export interface WorkspaceCurrencyPickerSheetProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (code: string) => void
  selectedCode?: string
}

export const WorkspaceCurrencyPickerSheet = ({
  isOpen,
  onClose,
  onSelect,
  selectedCode,
}: WorkspaceCurrencyPickerSheetProps) => {
  const { t } = useNls()
  const [search, setSearch] = useState('')
  const parentRef = useRef<HTMLDivElement>(null)

  const filteredCurrencies = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return ISO_4217_CURRENCIES
    return ISO_4217_CURRENCIES.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q),
    )
  }, [search])

  const rowVirtualizer = useVirtualizer({
    count: filteredCurrencies.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 8,
  })

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('NLSWorkspaceCurrencyShowAll', { count: ISO_4217_CURRENCIES.length }).replace(' →', '')}
      className="workspaceCurrencyPickerSheet"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          padding: '4px 0 16px',
        }}
      >
        {/* Search header */}
        <div
          style={{
            margin: '0 16px',
          }}
        >
          <TextInput
            type="search"
            role="searchbox"
            aria-label={t('NLSWorkspaceCurrencySearchPlaceholder')}
            placeholder={t('NLSWorkspaceCurrencySearchPlaceholder')}
            value={search}
            onChange={(next) => setSearch(next)}
            autoFocus
            leadingIcon={
              <SearchRoundedIcon
                style={{
                  color: 'var(--muted)',
                  fontSize: 20,
                }}
              />
            }
            trailingIcon={
              search ? (
                <IconButton
                  variant="tertiary"
                  size="sm"
                  label="Clear search"
                  icon={<CloseRoundedIcon style={{ fontSize: 16 }} />}
                  onClick={() => setSearch('')}
                />
              ) : undefined
            }
          />
        </div>

        {/* Virtualized Currency List */}
        <div
          ref={parentRef}
          role="listbox"
          aria-label="All currencies"
          style={{
            height: 400,
            maxHeight: '55vh',
            overflowY: 'auto',
            contain: 'strict',
            padding: '0 8px',
          }}
        >
          {filteredCurrencies.length === 0 ? (
            <div
              style={{
                padding: '32px 16px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: 14,
              }}
            >
              No currencies found matching &ldquo;{search}&rdquo;
            </div>
          ) : (
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const currency = filteredCurrencies[virtualRow.index]
                const isSelected = selectedCode?.toUpperCase() === currency.code
                return (
                  <div
                    key={currency.code}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      padding: '2px 8px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Pressable
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        onSelect(currency.code)
                        onClose()
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 12px',
                        borderRadius: 'var(--radius-ctl-sm, 8px)',
                        border: isSelected
                          ? '1px solid var(--accent)'
                          : '1px solid transparent',
                        background: isSelected
                          ? 'var(--accent-soft)'
                          : 'transparent',
                        color: 'inherit',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.12s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background =
                            'var(--surface-hover)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = 'transparent'
                        }
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          minWidth: 0,
                        }}
                      >
                        <span style={{ fontSize: 24, lineHeight: 1 }}>
                          {currency.flag}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                            }}
                          >
                            <strong
                              style={{
                                fontSize: 14,
                                color: 'var(--text-strong)',
                              }}
                            >
                              {currency.code}
                            </strong>
                            <span
                              style={{
                                fontSize: 12,
                                color: 'var(--muted)',
                              }}
                            >
                              · {currency.symbol}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--muted)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {currency.name}
                          </div>
                        </div>
                      </div>

                      {isSelected ? (
                        <CheckRoundedIcon
                          style={{
                            fontSize: 18,
                            color: 'var(--accent)',
                          }}
                        />
                      ) : null}
                    </Pressable>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  )
}
